import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useScope } from "../lib/scope";
import { api, ROLE_LABELS, formatApiErrorDetail, loadElementTypes } from "../lib/gmao";
import { Btn, Field, Input, Modal } from "./Shared";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { AparienciaModal } from "../themes/Apariencia";
import { PushToggle } from "./PushToggle";
import ScopeBar from "./ScopeBar";
import { toast } from "sonner";
import { NotificationBell } from "./NotificationBell";
import { OfflineBar } from "./OfflineBar";
import { Buildings, Stack, Warning, Wrench, GridFour, SignOut, ClipboardText, Receipt, UsersThree, QrCode, Key, Gear, LockKey, Sparkle, Archive, List, X, Palette, Coins, MagicWand } from "@phosphor-icons/react";

const PIN_ROLES = ["admin", "operator"];
const SCOPE_PREFIXES = ["/empresas", "/elementos", "/revisiones", "/incidencias", "/etiquetas", "/ordenes", "/instalaciones"];

const SCREEN_NAMES = [
  [/^\/$/, "Panel"], [/^\/empresas\/.+/, "Ficha de empresa"], [/^\/empresas/, "Empresas"],
  [/^\/instalaciones\/.+/, "Ficha de instalación"], [/^\/instalaciones/, "Instalaciones"],
  [/^\/elementos\/.+/, "Ficha de elemento"], [/^\/elementos/, "Elementos"],
  [/^\/revisiones/, "Revisiones"], [/^\/incidencias/, "Incidencias"],
  [/^\/ordenes/, "Órdenes de trabajo"], [/^\/ofertas/, "Ofertas"], [/^\/facturacion/, "Facturación"], [/^\/etiquetas/, "Etiquetas"],
  [/^\/usuarios/, "Usuarios"], [/^\/configuracion/, "Configuración"], [/^\/visitas/, "Visitas"], [/^\/asistente/, "Asistente IA"], [/^\/historicos/, "Históricos"],
];
const screenName = (path) => (SCREEN_NAMES.find(([re]) => re.test(path)) || [null, path])[1];

function MyPinModal({ open, onClose }) {
  const [pin, setPin] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(pin.trim())) { toast.error("El PIN debe tener entre 4 y 6 dígitos."); return; }
    setSaving(true);
    try {
      await api.put("/users/me/qr-pin", { pin: pin.trim() });
      toast.success("PIN de revisión actualizado.");
      setPin(""); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "No se pudo guardar el PIN."); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Mi PIN de revisión (QR)">
      <form onSubmit={save} className="space-y-4" data-testid="my-pin-form">
        <p className="text-sm text-slate-500">Lo usarás para registrar revisiones rápidas escaneando el QR de cada elemento. Solo tú debes conocerlo.</p>
        <Field label="Nuevo PIN (4-6 dígitos)">
          <Input value={pin} onChange={(e) => setPin(e.target.value)} inputMode="numeric" maxLength={6} placeholder="Ej.: 2468" data-testid="my-pin-input" />
        </Field>
        <div className="flex justify-end gap-2">
          <Btn variant="secondary" type="button" onClick={onClose}>Cancelar</Btn>
          <Btn type="submit" disabled={saving} data-testid="my-pin-save">{saving ? "Guardando…" : "Guardar PIN"}</Btn>
        </div>
      </form>
    </Modal>
  );
}

const NAV = [
  { to: "/", label: "Panel", icon: GridFour, end: true, roles: ["admin", "gestor", "operator"] },
  { to: "/empresas", label: "Empresas", icon: Buildings, roles: ["admin", "gestor", "operator", "client_group", "client_plant"] },
  { to: "/alta", label: "Alta guiada", icon: MagicWand, roles: ["admin", "gestor"] },
  { to: "/elementos", label: "Elementos", icon: Stack, roles: ["admin", "gestor", "operator", "client_group", "client_plant"] },
  { to: "/revisiones", label: "Revisiones", icon: ClipboardText, roles: ["admin", "gestor", "operator", "client_group", "client_plant"] },
  { to: "/incidencias", label: "Incidencias", icon: Warning, roles: ["admin", "gestor", "operator", "client_group", "client_plant"] },
  { to: "/ordenes", label: "Órdenes de trabajo", icon: Wrench, roles: ["admin", "operator", "client_group", "client_plant"] },
  { to: "/ofertas", label: "Ofertas", icon: Receipt, roles: ["admin"] },
  { to: "/facturacion", label: "Facturación", icon: Coins, roles: ["admin"] },
  { to: "/etiquetas", label: "Etiquetas", icon: QrCode, roles: ["admin"] },
  { to: "/usuarios", label: "Usuarios", icon: UsersThree, roles: ["admin"] },
  { to: "/historicos", label: "Históricos", icon: Archive, roles: ["admin"] },
  { to: "/asistente", label: "Asistente IA", icon: Sparkle, roles: ["admin"] },
  { to: "/configuracion", label: "Configuración", icon: Gear, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [, setTypesV] = useState(0);
  // Carga los tipos de elemento configurables una vez y re-renderiza el árbol para
  // que desplegables, leyendas del plano y checklists reflejen los tipos personalizados.
  useEffect(() => { loadElementTypes().then((d) => { if (d) setTypesV((v) => v + 1); }); }, []);
  const showScope = SCOPE_PREFIXES.some((p) => loc.pathname === p || (p !== "/instalaciones" && loc.pathname.startsWith(p + "/")));
  const [overdue, setOverdue] = useState(0);
  const [openInc, setOpenInc] = useState(0);
  const [myInc, setMyInc] = useState(0);
  const [pinModal, setPinModal] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [aparienciaModal, setAparienciaModal] = useState(false);
  const seenKey = `gmao:myinc:seen:${user?.id || ""}`;
  const loadStats = useCallback(() => {
    api.get("/dashboard/stats").then((r) => {
      setOverdue(r.data?.revisions_overdue?.length || 0);
      setOpenInc(r.data?.open_incidents || 0);
      setMyInc(r.data?.my_assigned_incidents || 0);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadStats(); }, [loadStats, loc.pathname]);
  useEffect(() => {
    const h = () => loadStats();
    window.addEventListener("gmao:data-changed", h);
    const t = setInterval(loadStats, 60000); // refresco periódico: nuevas asignaciones visibles sin navegar
    return () => { window.removeEventListener("gmao:data-changed", h); clearInterval(t); };
  }, [loadStats]);
  // «Nuevas» = asignadas por encima de las ya vistas la última vez que se abrió Incidencias
  const seen = Number(localStorage.getItem(seenKey) || 0);
  useEffect(() => { if (loc.pathname.startsWith("/incidencias")) localStorage.setItem(seenKey, String(myInc)); }, [loc.pathname, myInc, seenKey]);
  const hasNewAssigned = myInc > seen && !loc.pathname.startsWith("/incidencias");
  const isOperator = user?.role === "operator";

  // ── Registro de visitas: pantalla consultada (con instalación en contexto), latido y cierre por inactividad ──
  const scope = useScope();
  const inactMinRef = useRef(30);
  useEffect(() => {
    api.get("/settings").then((r) => {
      const v = parseInt(r.data?.inactivity_minutes, 10);
      if (!isNaN(v)) inactMinRef.current = v;
    }).catch(() => {});
  }, []);
  useEffect(() => {
    api.post("/visits/track", {
      screen: screenName(loc.pathname), path: loc.pathname,
      installation_id: scope?.installId || null, company_id: scope?.companyId || null,
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, scope?.installId]);
  useEffect(() => {
    const t = setInterval(() => api.post("/visits/heartbeat").catch(() => {}), 120000);
    return () => clearInterval(t);
  }, []);
  const lastActRef = useRef(Date.now());
  useEffect(() => {
    const mark = () => { lastActRef.current = Date.now(); };
    const evs = ["click", "keydown", "mousemove", "scroll", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    const t = setInterval(() => {
      const mins = inactMinRef.current;
      if (mins > 0 && Date.now() - lastActRef.current > mins * 60000) {
        logout("inactividad");
        nav("/login");
        toast.info("Sesión cerrada por inactividad.");
      }
    }, 30000);
    return () => { evs.forEach((e) => window.removeEventListener(e, mark)); clearInterval(t); };
  }, [logout, nav]);

  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const navItems = useMemo(() => NAV.filter((n) => !n.roles || n.roles.includes(user?.role)), [user?.role]);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  return (
    <div className="min-h-screen flex">
      {menuOpen && <div className="fixed inset-0 bg-slate-900/60 z-30 lg:hidden" onClick={() => setMenuOpen(false)} data-testid="sidebar-overlay" />}
      <aside className={`w-60 shrink-0 bg-slate-900 text-slate-100 flex flex-col fixed h-screen z-40 overflow-y-auto transition-transform duration-200 ${menuOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-800 flex items-start justify-between">
          <div>
            <div className="font-head text-xl font-bold tracking-tight text-white">GMAO-FOR Design</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-0.5">Mantenimiento logístico</div>
          </div>
          <button onClick={() => setMenuOpen(false)} className="lg:hidden text-slate-400 hover:text-white p-1" aria-label="Cerrar menú" data-testid="sidebar-close-btn"><X size={20} /></button>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-150 ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}>
              <n.icon size={18} weight="bold" />
              <span className="flex-1">{n.label}</span>
              {n.to === "/revisiones" && overdue > 0 && (
                <span className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center text-[11px] font-bold text-white bg-red-600 rounded-full" data-testid="overdue-badge" title={`${overdue} revisiones vencidas`}>{overdue}</span>
              )}
              {n.to === "/incidencias" && (isOperator ? myInc > 0 : (openInc > 0 || myInc > 0)) && (
                <span className="ml-auto inline-flex items-center gap-1">
                  {!isOperator && openInc > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center text-[11px] font-bold text-white bg-red-600 rounded-full" data-testid="incidents-badge" title={`${openInc} incidencias abiertas`}>{openInc}</span>
                  )}
                  {myInc > 0 && (
                    <span className={`min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center text-[11px] font-bold text-white bg-orange-500 rounded-full ${hasNewAssigned ? "animate-pulse ring-2 ring-orange-300" : ""}`}
                      data-testid="my-incidents-badge" title={`${myInc} reparación/es asignada/s a ti${hasNewAssigned ? " · hay nuevas" : ""}`}>{myInc}</span>
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 bg-blue-600 flex items-center justify-center font-mono text-sm font-bold text-white rounded-sm">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-white truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{ROLE_LABELS[user?.role] || user?.role}</div>
            </div>
          </div>
          {PIN_ROLES.includes(user?.role) && <PushToggle />}
          {PIN_ROLES.includes(user?.role) && (
            <button onClick={() => setPinModal(true)} data-testid="my-pin-btn"
              className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-all">
              <Key size={16} /> Mi PIN de revisión
            </button>
          )}
          {user?.role === "admin" && <NotificationBell />}
          <button onClick={() => setAparienciaModal(true)} data-testid="apariencia-btn"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-all">
            <Palette size={16} /> Apariencia
          </button>
          <button onClick={() => setPwModal(true)} data-testid="change-password-btn"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-all">
            <LockKey size={16} /> Cambiar contraseña
          </button>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn"
            className="mt-1 w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm transition-all">
            <SignOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 ml-0 lg:ml-60 min-h-screen blueprint">
        <OfflineBar />
        <div className="lg:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-slate-900 text-white shadow-md" data-testid="mobile-topbar">
          <button onClick={() => setMenuOpen(true)} className="p-1 -ml-1 text-slate-200 hover:text-white" aria-label="Abrir menú" data-testid="sidebar-open-btn"><List size={22} weight="bold" /></button>
          <div className="font-head text-lg font-bold tracking-tight">GMAO-FOR Design</div>
          <div className="ml-auto text-xs text-slate-400 truncate max-w-[45%]">{screenName(loc.pathname)}</div>
          {user?.role === "admin" && <NotificationBell compact />}
        </div>
        <div className="p-4 sm:p-6 lg:p-8 w-full">{showScope && <ScopeBar />}{children}</div>
      </main>
      <MyPinModal open={pinModal} onClose={() => setPinModal(false)} />
      <ChangePasswordModal open={pwModal} onClose={() => setPwModal(false)} />
      <AparienciaModal open={aparienciaModal} onClose={() => setAparienciaModal(false)} />
    </div>
  );
}

export function PageHeader({ title, sub, actions, testid }) {
  return (
    <div className="flex items-end justify-between mb-6 flex-wrap gap-4" data-testid={testid}>
      <div>
        <h1 className="font-head text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
      </div>
      <div className="flex gap-2">{actions}</div>
    </div>
  );
}
